import { createApi } from '@reduxjs/toolkit/query/react'
import { baseQueryWithTokenRefresh } from '../../common/baseQuery'
import { type AccountVocabulary, type AccountVocabularyAddResult } from '../../models/AccountVocabulary'
import { type BaseRequest, type BaseResponse } from '../models'
import { accountUserSettingsApi } from '../accountUserSettings/accountUserSettings'

const _BASE_URL = import.meta.env.REACT_APP_IAM_API_BASE_URL!

export interface AddVocabularyTermsRequest extends BaseRequest {
  terms: string[]
}

export interface RenameVocabularyTermRequest extends BaseRequest {
  term: string
  newTerm: string
}

export interface RemoveVocabularyTermRequest extends BaseRequest {
  term: string
}

const vocabularyUrl = (accountId: string) => `/accounts/${accountId}/vocabulary`
const termUrl = (accountId: string, term: string) => `${vocabularyUrl(accountId)}/terms/${encodeURIComponent(term)}`

export const accountVocabularyApi = createApi({
  reducerPath: 'accountVocabularyApi',
  baseQuery: baseQueryWithTokenRefresh(_BASE_URL),
  tagTypes: ['AccountVocabulary'],
  endpoints: (builder) => {
    const replaceCachedVocabulary = async (
      { accountId }: BaseRequest,
      { dispatch, queryFulfilled }: { dispatch: (action: unknown) => unknown; queryFulfilled: Promise<{ data: BaseResponse<AccountVocabulary> }> },
    ) => {
      try {
        const { data } = await queryFulfilled
        const { system, enterprise, account, locked, effective, limits } = data.data
        dispatch(
          accountVocabularyApi.util.updateQueryData('getVocabulary', { accountId }, (draft) => {
            draft.data = { system, enterprise, account, locked, effective, limits }
          }),
        )
        dispatch(accountUserSettingsApi.util.invalidateTags(['Account']))
      } catch {
        return
      }
    }

    return {
      getVocabulary: builder.query<BaseResponse<AccountVocabulary>, BaseRequest>({
        query: ({ accountId }) => ({ url: vocabularyUrl(accountId) }),
        providesTags: ['AccountVocabulary'],
      }),
      addVocabularyTerms: builder.mutation<BaseResponse<AccountVocabularyAddResult>, AddVocabularyTermsRequest>({
        query: ({ accountId, terms }) => ({ url: `${vocabularyUrl(accountId)}/terms`, method: 'POST', body: { terms } }),
        onQueryStarted: replaceCachedVocabulary,
      }),
      renameVocabularyTerm: builder.mutation<BaseResponse<AccountVocabulary>, RenameVocabularyTermRequest>({
        query: ({ accountId, term, newTerm }) => ({ url: termUrl(accountId, term), method: 'PUT', body: { term: newTerm } }),
        onQueryStarted: replaceCachedVocabulary,
      }),
      removeVocabularyTerm: builder.mutation<BaseResponse<AccountVocabulary>, RemoveVocabularyTermRequest>({
        query: ({ accountId, term }) => ({ url: termUrl(accountId, term), method: 'DELETE' }),
        onQueryStarted: replaceCachedVocabulary,
      }),
    }
  },
})

export const { useGetVocabularyQuery, useAddVocabularyTermsMutation, useRenameVocabularyTermMutation, useRemoveVocabularyTermMutation } = accountVocabularyApi
